import { z } from 'zod';

import type { ObjectivePeriodStatusType } from '@/constants/ObjectivePeriodStatus';
import type { TranslationKey } from '@/i18n-key';
import type { ObjectivePeriod } from '@/types/objective-periods';

import { ObjectivePeriodStatus } from '@/constants/ObjectivePeriodStatus';
import { validateUniqueLocalizedName } from '@/utils/localizedNameValidation';

export const NAME_MAX_LENGTH = 100;

const NAME_REQUIRED_MESSAGE: TranslationKey = 'objectivePeriods.nameRequired';
const NAME_MAX_LENGTH_MESSAGE: TranslationKey = 'objectivePeriods.nameMaxLength';
const NAME_DUPLICATE_MESSAGE: TranslationKey = 'objectivePeriods.nameDuplicateError';
const START_DATE_REQUIRED_MESSAGE: TranslationKey = 'objectivePeriods.startDateRequired';
const END_DATE_REQUIRED_MESSAGE: TranslationKey = 'objectivePeriods.endDateRequired';
const END_DATE_BEFORE_START_MESSAGE: TranslationKey = 'objectivePeriods.endDateBeforeStartDate';

export type ObjectivePeriodFormData = {
    name: string;
    startDate: string;
    endDate: string;
    status: ObjectivePeriodStatusType;
};

export interface ObjectivePeriodSchemaParams {
    existingPeriods: ObjectivePeriod[];
    currentPeriodId: string | undefined;
    currentLanguage: string;
}

/**
 * Creates the Zod schema for the AddEditObjectivePeriodModal form. It depends on runtime values
 * (the existing periods, the language being edited) so it is returned by a factory.
 */
export const createObjectivePeriodSchema = ({
    existingPeriods,
    currentPeriodId,
    currentLanguage,
}: ObjectivePeriodSchemaParams) =>
    z
        .object({
            // Trimmed first: a name of spaces alone is no name, and the saved dictionary drops it.
            name: z
                .string()
                .trim()
                .min(1, { message: NAME_REQUIRED_MESSAGE })
                .max(NAME_MAX_LENGTH, { message: NAME_MAX_LENGTH_MESSAGE })
                // The backend enforces the same rule per language and answers 400
                // (`ObjectivePeriodDuplicateNameError`).
                .superRefine(
                    validateUniqueLocalizedName({
                        existing: existingPeriods,
                        currentId: currentPeriodId,
                        language: currentLanguage,
                        message: NAME_DUPLICATE_MESSAGE,
                    }),
                ),
            startDate: z.string().min(1, { message: START_DATE_REQUIRED_MESSAGE }),
            endDate: z.string().min(1, { message: END_DATE_REQUIRED_MESSAGE }),
            // No default here: the form always starts from a status, `EMPTY_FORM`'s on creation and
            // the period's own on edit.
            status: z.enum([
                ObjectivePeriodStatus.DRAFT,
                ObjectivePeriodStatus.ACTIVE,
                ObjectivePeriodStatus.DEACTIVATED,
                ObjectivePeriodStatus.COMPLETED,
            ]),
        })
        // A period is a closed window: both dates are required, and the end can never precede the
        // start. Compared as `yyyy-MM-dd` strings, which sort chronologically.
        .superRefine(({ startDate, endDate }, ctx) => {
            if (startDate && endDate && endDate < startDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: END_DATE_BEFORE_START_MESSAGE,
                    path: ['endDate'],
                });
            }
        });
